// THE GUIDED DEAL on screen (ux-guided-deal): the progress bar, the next-step
// card and the collapsible journey sections of the quote screen, plus the
// dismissible "How this works" panel the OEM pages share.
//
// Everything shown is derived in lib/dealProgress.ts from data the screen
// already read. ⚠️ These components decide nothing and send nothing: the
// card's button calls an action the screen already had (or scrolls to it).

import { useEffect, useRef, useState } from "react";
import { AlertTriangle, Check, ChevronDown, ChevronRight, Circle, Clock, X } from "lucide-react";
import {
  STATE_LABEL, type DealStep, type NextAction, type StepKey, type StepState,
} from "@/lib/dealProgress";
import { colors, semanticColors, radius, spacing, heights, typography, iconSize, iconStroke } from "@/styles/tokens";

export const STEP_TONE: Record<StepState, { fg: string; bg: string; border: string }> = {
  done: { fg: colors.green600, bg: colors.green50, border: colors.green100 },
  current: { fg: semanticColors.accentTextOnTint, bg: semanticColors.accentBg, border: colors.accentInk },
  waiting: { fg: colors.amber800, bg: colors.amber50, border: colors.amber200 },
  todo: { fg: semanticColors.textMuted, bg: semanticColors.surface, border: semanticColors.border },
  blocked: { fg: colors.red600, bg: colors.red50, border: colors.red100 },
};

function StateIcon({ state }: { state: StepState }) {
  const p = { size: iconSize.md, strokeWidth: iconStroke.regular, "aria-hidden": true } as const;
  if (state === "done") return <Check {...p} />;
  if (state === "waiting") return <Clock {...p} />;
  if (state === "blocked") return <AlertTriangle {...p} />;
  return <Circle {...p} />;
}

/** "Waiting for the OEM" etc.: the state in words, for the pill and screen readers. */
export function stateWords(s: DealStep): string {
  if (s.state === "waiting") return `Waiting for ${s.waitingOn ?? "someone else"}`;
  return STATE_LABEL[s.state];
}

export function StepStatePill({ step, testId }: { step: DealStep; testId?: string }) {
  const t = STEP_TONE[step.state];
  return (
    <span data-testid={testId} style={{
      display: "inline-flex", alignItems: "center", gap: spacing[4], height: heights.pill,
      padding: `0 ${spacing[8]}px`, borderRadius: radius.base, whiteSpace: "nowrap",
      fontSize: typography.fontSize.xs, fontWeight: typography.fontWeight.medium,
      color: t.fg, backgroundColor: t.bg, border: `1px solid ${t.border}`,
    }}><StateIcon state={step.state} />{stateWords(step)}</span>
  );
}

export const sectionId = (key: StepKey) => `deal-step-${key}`;

/** THE DEAL PROGRESS BAR: every step, its state and a one-line summary. */
export function DealProgressBar({ steps, focus, onGo }: {
  steps: DealStep[]; focus?: StepKey; onGo: (key: StepKey) => void;
}) {
  return (
    <nav data-testid="deal-progress" aria-label="Deal progress" style={{ marginTop: spacing[12] }}>
      <div style={{ fontSize: typography.fontSize.xs, color: semanticColors.textMuted, marginBottom: spacing[6] }}>
        Where this deal is. OEM = the manufacturer or supplier we buy from. Click a step to go to it.
      </div>
      <ol style={{
        listStyle: "none", margin: 0, padding: 0, display: "grid", gap: spacing[6],
        gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
      }}>
        {steps.map((s) => {
          const t = STEP_TONE[s.state];
          const isFocus = s.key === focus;
          return (
            <li key={s.key} style={{ minWidth: 0 }}>
              <button
                type="button"
                data-testid={`deal-step-${s.key}`}
                data-state={s.state}
                aria-current={isFocus ? "step" : undefined}
                aria-label={`Step ${s.no} of ${steps.length}, ${s.title}: ${stateWords(s)}. ${s.summary.replace(/\.$/, "")}. Go to this step.`}
                onClick={() => onGo(s.key)}
                style={{
                  width: "100%", height: "100%", textAlign: "left", cursor: "pointer",
                  display: "flex", flexDirection: "column", gap: spacing[2],
                  padding: `${spacing[6]}px ${spacing[8]}px`, borderRadius: radius.base,
                  backgroundColor: t.bg, color: semanticColors.textPrimary,
                  border: `${isFocus ? 2 : 1}px solid ${t.border}`,
                }}
              >
                <span style={{ display: "flex", alignItems: "center", gap: spacing[4], color: t.fg,
                  fontSize: typography.fontSize.xs, fontWeight: typography.fontWeight.medium }}>
                  <StateIcon state={s.state} />
                  <span>Step {s.no} · {stateWords(s)}</span>
                </span>
                <span style={{ fontSize: typography.fontSize.sm, fontWeight: typography.fontWeight.semibold }}>{s.title}</span>
                <span data-testid={`deal-step-${s.key}-summary`}
                  style={{ fontSize: typography.fontSize.xs, color: semanticColors.textSecondary, overflowWrap: "anywhere" }}>
                  {s.summary}
                </span>
              </button>
            </li>
          );
        })}
      </ol>
    </nav>
  );
}

/** THE NEXT STEP CARD: what to do now, one button, and what the click does. */
export function NextStepCard({ action, busy, error, onAct }: {
  action: NextAction; busy?: boolean; error?: string;
  onAct: (kind: NextAction["kind"], which: "primary" | "secondary") => void;
}) {
  const btn: React.CSSProperties = {
    height: heights.control, padding: `0 ${spacing[14]}px`, borderRadius: radius.base,
    fontSize: typography.fontSize.base, fontWeight: typography.fontWeight.medium,
    cursor: busy ? "not-allowed" : "pointer", whiteSpace: "nowrap",
  };
  return (
    <section data-testid="next-step" aria-label="What to do now" style={{
      marginTop: spacing[8], padding: `${spacing[10]}px ${spacing[12]}px`,
      border: `1px solid ${semanticColors.accentBorder}`, borderRadius: radius.base,
      backgroundColor: semanticColors.surface, display: "grid", gap: spacing[6],
    }}>
      <div style={{ fontSize: typography.fontSize.xs, color: semanticColors.textMuted, fontWeight: typography.fontWeight.medium }}>
        What to do now
      </div>
      <div data-testid="next-step-text" role="status"
        style={{ fontSize: typography.fontSize.md, color: semanticColors.textPrimary, fontWeight: typography.fontWeight.medium }}>
        {action.text}
      </div>
      {action.checklist && action.checklist.length > 0 && (
        <ul data-testid="next-step-checklist" style={{ margin: 0, paddingLeft: 0, listStyle: "none", display: "grid", gap: spacing[4] }}>
          {action.checklist.map((c, i) => (
            <li key={i} style={{ display: "flex", gap: spacing[6], alignItems: "flex-start",
              fontSize: typography.fontSize.sm, color: colors.red600 }}>
              <X size={iconSize.md} strokeWidth={iconStroke.regular} aria-hidden style={{ flexShrink: 0, marginTop: spacing[2] }} />
              <span>{c}</span>
            </li>
          ))}
        </ul>
      )}
      {(action.kind !== "none" || action.secondary) && (
        <div style={{ display: "flex", gap: spacing[8], flexWrap: "wrap", alignItems: "center" }}>
          {action.kind !== "none" && action.button && (
            <button type="button" data-testid="next-step-primary" disabled={busy}
              onClick={() => onAct(action.kind, "primary")}
              style={{ ...btn, border: "none", backgroundColor: colors.indigo600, color: semanticColors.onAccent }}>
              {action.button}
            </button>
          )}
          {action.secondary && (
            <button type="button" data-testid="next-step-secondary" disabled={busy}
              onClick={() => onAct(action.secondary!.kind, "secondary")}
              title={action.secondary.consequence}
              style={{ ...btn, border: `1px solid ${semanticColors.border}`, backgroundColor: semanticColors.surface,
                color: semanticColors.textSecondary }}>
              {action.secondary.button}
            </button>
          )}
        </div>
      )}
      {action.consequence && action.kind !== "none" && (
        <div data-testid="next-step-consequence" style={{ fontSize: typography.fontSize.sm, color: semanticColors.textSecondary }}>
          <strong style={{ fontWeight: typography.fontWeight.medium }}>What happens when you click:</strong> {action.consequence}
        </div>
      )}
      {error && <div data-testid="next-step-error" role="alert" style={{ fontSize: typography.fontSize.sm, color: colors.red600 }}>{error}</div>}
    </section>
  );
}

/** ONE JOURNEY SECTION. The current step is open, the others collapse to their
 *  summary line. ⚠️ A collapsed section stays MOUNTED (only hidden), so every
 *  panel inside keeps loading, and every control is one click away. */
export function DealSection({ step, openSignal = 0, alwaysOpen = false, header, children }: {
  step: DealStep | undefined;
  /** Bumped to open the section from outside (the bar, the card). */
  openSignal?: number;
  alwaysOpen?: boolean;
  /** Shown in the header row whether open or not (a compact bar). */
  header?: React.ReactNode;
  children?: React.ReactNode;
}) {
  const wantOpen = alwaysOpen || (!!step && (step.state === "current" || step.state === "blocked" || step.state === "waiting"));
  const [open, setOpen] = useState(wantOpen);
  // A change of the step's state re-decides; the user's own toggle holds until then.
  const lastState = useRef(step?.state);
  useEffect(() => {
    if (lastState.current !== step?.state) { lastState.current = step?.state; setOpen(wantOpen); }
  }, [step?.state, wantOpen]);
  useEffect(() => { if (openSignal > 0) setOpen(true); }, [openSignal]);
  if (!step) return <>{children}</>;
  const key = step.key;
  const bodyId = `${sectionId(key)}-body`;
  const shown = alwaysOpen || open;
  return (
    <section id={sectionId(key)} data-testid={`deal-section-${key}`} data-open={shown ? "true" : "false"}
      aria-labelledby={`${sectionId(key)}-title`}
      style={{ marginTop: spacing[16], scrollMarginTop: spacing[16] }}>
      <div style={{ display: "flex", alignItems: "center", gap: spacing[8], flexWrap: "wrap",
        paddingBottom: spacing[6], borderBottom: `1px solid ${semanticColors.border}` }}>
        {alwaysOpen ? (
          <span id={`${sectionId(key)}-title`} style={{ fontSize: typography.fontSize.md, fontWeight: typography.fontWeight.semibold,
            color: semanticColors.textPrimary }}>Step {step.no}: {step.title}</span>
        ) : (
          <button type="button" id={`${sectionId(key)}-title`} data-testid={`deal-section-${key}-toggle`}
            aria-expanded={shown} aria-controls={bodyId} onClick={() => setOpen((o) => !o)}
            style={{ display: "inline-flex", alignItems: "center", gap: spacing[4], background: "none", border: "none",
              padding: 0, cursor: "pointer", fontSize: typography.fontSize.md, fontWeight: typography.fontWeight.semibold,
              color: semanticColors.textPrimary }}>
            {shown ? <ChevronDown size={iconSize.lg} strokeWidth={iconStroke.regular} aria-hidden />
              : <ChevronRight size={iconSize.lg} strokeWidth={iconStroke.regular} aria-hidden />}
            Step {step.no}: {step.title}
          </button>
        )}
        <StepStatePill step={step} testId={`deal-section-${key}-state`} />
        <span data-testid={`deal-section-${key}-summary`}
          style={{ fontSize: typography.fontSize.sm, color: semanticColors.textSecondary, overflowWrap: "anywhere" }}>
          {step.summary}
        </span>
      </div>
      {header}
      <div id={bodyId} hidden={!shown}>{children}</div>
    </section>
  );
}

/** "How this works": dismissible, remembered per browser. Storage is a
 *  convenience: a browser that blocks it simply shows the panel again. */
export function HowItWorks({ storageKey, title = "How this works", children, testId = "how-it-works" }: {
  storageKey: string; title?: string; children: React.ReactNode; testId?: string;
}) {
  const [hidden, setHidden] = useState<boolean>(() => {
    try { return window.localStorage.getItem(storageKey) === "dismissed"; } catch { return false; }
  });
  const toggle = (next: boolean) => {
    setHidden(next);
    try {
      if (next) window.localStorage.setItem(storageKey, "dismissed");
      else window.localStorage.removeItem(storageKey);
    } catch { /* storage blocked: the choice lasts for this visit */ }
  };
  if (hidden) {
    return (
      <button type="button" data-testid={`${testId}-show`} onClick={() => toggle(false)} style={{
        marginBottom: spacing[12], background: "none", border: "none", padding: 0, cursor: "pointer",
        fontSize: typography.fontSize.sm, color: semanticColors.accentInk, textDecoration: "underline",
      }}>{title}</button>
    );
  }
  return (
    <aside data-testid={testId} aria-label={title} style={{
      marginBottom: spacing[16], padding: `${spacing[10]}px ${spacing[12]}px`, borderRadius: radius.base,
      border: `1px solid ${semanticColors.accentBorder}`, backgroundColor: semanticColors.accentBg,
      color: semanticColors.accentTextOnTint, fontSize: typography.fontSize.sm, display: "grid", gap: spacing[6],
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: spacing[8] }}>
        <strong style={{ flex: 1, fontWeight: typography.fontWeight.semibold }}>{title}</strong>
        <button type="button" data-testid={`${testId}-dismiss`} onClick={() => toggle(true)} aria-label={`Hide ${title}`}
          style={{ height: heights.iconButton, display: "inline-flex", alignItems: "center", gap: spacing[4],
            background: "none", border: "none", cursor: "pointer", color: semanticColors.accentTextOnTint,
            fontSize: typography.fontSize.sm }}>
          <X size={iconSize.md} strokeWidth={iconStroke.regular} aria-hidden /> Hide
        </button>
      </div>
      {children}
    </aside>
  );
}
